import axios from 'axios';
import swal from 'sweetalert';

import { addTrailingSlashToUrl } from '../../components/common/Route';
import * as Constants from '../helpers/Constants';

import Auth from './Auth';
import Storage, { deleteCRMCredentials, saveCRMCredentials } from './Storage';

import waitUntil from 'crm/helpers/waitUntil';

const axiosCRM = axios.create();

// Add a request interceptor
axiosCRM.interceptors.request.use(
    (config) => {
        const token = Storage.load('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Content-Type'] = 'application/json';

        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

// Add a response interceptors
axiosCRM.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const loginUrl = addTrailingSlashToUrl(`${Constants.URL}/login`);

        switch (error.response?.status) {
            case 401: {
                const accessToken = await refreshAccessToken();
                if (accessToken) {
                    error.config.headers.Authorization = `Bearer ${accessToken}`;
                    return axios(error.config);
                }

                if (window.location.pathname !== loginUrl) {
                    window.location.href = loginUrl;
                }
                break;
            }

            case 403:
                if (window.location.pathname !== loginUrl) {
                    swal({
                        title: 'Forbidden!',
                        text: 'You are not allowed to see this resource!',
                        icon: 'error',
                        buttons: 'Close' as any, // error in library, we should upgrade to sweetalert2
                        dangerMode: true,
                    }).then(() => {
                        Auth.signOut(axiosCRM);
                    });
                }
                break;

            default:
        }

        return Promise.reject(error);
    }
);

let refreshLocked = false;

async function refreshAccessToken(): Promise<string | null> {
    const accessToken = Storage.load('access_token');
    if (!accessToken) {
        return null;
    }

    if (!refreshLocked) {
        refreshLocked = true;
        try {
            const { data } = await axios.post(addTrailingSlashToUrl(`${Constants.URL}/refresh_token`), {
                grant_type: 'refresh_token',
                access_token: accessToken,
                refresh_token: Storage.load('refresh_token'),
            });
            saveCRMCredentials(data.access_token, data.refresh_token, data.token_type, data.expires_in);
        } catch {
            deleteCRMCredentials();
        } finally {
            refreshLocked = false;
        }
    } else {
        // if there are other requests calling this function,
        // they must wait until the first one refreshes the token
        await waitUntil(() => !refreshLocked, 6000, 500);
    }

    return Storage.load('access_token');
}

export default axiosCRM;
